import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Scale, Ban } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { PermissionDenied, Skeleton } from '@/components/States'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { fetchFairness, BAN_STR as S, type FairnessData } from './strikesApi'

/**
 * A8.20 — Adolat hisoboti (ban/unban nisbati). JONLI `/admin/appeals` +
 * `/admin/banned` ustidan client-side agregat: rassmotreno, doля otmen
 * (granted/decided), aktiv banlar.
 */
export function FairnessReportScreen() {
  const { can } = useAuth()
  const [data, setData] = useState<FairnessData | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    fetchFairness(ac.signal)
      .then((d) => alive && setData(d))
      .catch(() => {})
    return () => {
      alive = false
      ac.abort()
    }
  }, [])

  const overturnPct = useMemo(() => {
    if (!data || data.appealsDecided === 0) return 0
    return Math.round((data.granted / data.appealsDecided) * 100)
  }, [data])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />
  const loading = data === null

  return (
    <>
      <PageHeader title={S.fairness} screenId="A8.20" description={S.fairnessNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mb-4 flex flex-wrap gap-3">
          <Tile label={S.bannedActive} value={data?.bannedActive ?? 0} loading={loading} tone="danger" icon={<Ban size={18} className="text-danger" />} />
          <Tile label={S.appealsTotal} value={data?.appealsTotal ?? 0} loading={loading} icon={<Scale size={18} className="text-accent" />} />
          <Tile label={S.appealsDecided} value={data?.appealsDecided ?? 0} loading={loading} />
          <Tile label={S.pending} value={data?.pending ?? 0} loading={loading} />
        </div>

        {/* Doля otmen */}
        <div className="mb-4 max-w-md rounded-lg border border-border-default bg-surface-2 p-4">
          <div className="mb-2 flex items-baseline justify-between">
            <span className="text-sm text-secondary">{S.overturnRate}</span>
            {loading ? (
              <Skeleton className="h-6 w-12" />
            ) : (
              <span className="font-mono text-xl tabular text-warning">{overturnPct}%</span>
            )}
          </div>
          <span className="block h-2 overflow-hidden rounded-full bg-surface-3">
            {!loading && (
              <span className="block h-full rounded-full bg-warning" style={{ width: `${overturnPct}%` }} />
            )}
          </span>
          <div className="mt-2 flex justify-between text-xs text-tertiary">
            <span>
              {S.granted}: <span className="font-mono text-success">{data?.granted ?? 0}</span>
            </span>
            <span>
              {S.rejected}: <span className="font-mono text-secondary">{data?.rejected ?? 0}</span>
            </span>
          </div>
        </div>

        {/* Otmeny po tipu */}
        <div className="max-w-md overflow-hidden rounded-md border border-border-default">
          <div className="border-b border-border-default bg-surface-2 px-3 py-2 text-xs font-medium text-tertiary">
            {S.granted}
          </div>
          <Row label={S.banAppeals} value={data?.banGranted ?? 0} loading={loading} />
          <Row label={S.strikeAppeals} value={data?.strikeGranted ?? 0} loading={loading} />
        </div>
      </div>
    </>
  )
}

function Tile({
  label,
  value,
  loading,
  tone,
  icon,
}: {
  label: string
  value: number
  loading: boolean
  tone?: 'danger'
  icon?: ReactNode
}) {
  const color = tone === 'danger' ? 'text-danger' : 'text-primary'
  return (
    <div className="inline-flex items-center gap-3 rounded-lg border border-border-default bg-surface-2 px-4 py-3">
      {icon}
      <div>
        <div className="text-xs text-tertiary">{label}</div>
        {loading ? (
          <Skeleton className="mt-1 h-6 w-12" />
        ) : (
          <div className={`font-mono text-xl tabular ${color}`}>{value.toLocaleString()}</div>
        )}
      </div>
    </div>
  )
}

function Row({ label, value, loading }: { label: string; value: number; loading: boolean }) {
  return (
    <div className="flex items-center justify-between border-b border-border-subtle px-3 py-2 text-sm last:border-0">
      <span className="text-secondary">{label}</span>
      {loading ? <Skeleton className="h-4 w-8" /> : <span className="font-mono tabular text-primary">{value}</span>}
    </div>
  )
}
